import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { isEmpty, get } from "lodash";
import {
  DatalistField,
  FormSelectField,
  FormTextField,
} from "../../pages/ReferralInbox/components/ReferralDataForm/components/FormComponents";
import { TITLE_OPTIONS } from "../../constants";
import { getReferrerLocationsService } from "../../services/workflow";
import {
  getReferrerName,
  isReferrerOutsideClinic,
  labelReferrers,
  scopeReferrersToClinic,
} from "../../utils/referrerClinicScope";

const ReferrerForPatientForm = ({ referrer, handleValueChange, updateResource, clinicId }) => {
  const isDataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";
  const referrers = useSelector(({ app: { referrers } }) => referrers);
  const [referrerOptions, setReferrerOptions] = useState([]);
  const [isLoadingProviderNumber, setIsLoadingProviderNumber] = useState(false);

  const referrerName =
    referrer.firstName || referrer.lastName ? `${referrer.firstName || ""} ${referrer.lastName || ""}`.trim() : "";

  const referrerEmail = referrers.find(({ id }) => id === referrer.id)?.email;

  const currentReferrer = referrers.find(({ id }) => id === referrer.id);
  const isCurrentReferrerOutsideClinic = isReferrerOutsideClinic(currentReferrer, clinicId);

  const labelledReferrers = useMemo(
    () => labelReferrers(scopeReferrersToClinic(referrers, clinicId, currentReferrer), referrers),
    [referrers, clinicId, currentReferrer]
  );

  const onReferrerSelected = useCallback(
    async (selectedItem) => {
      const match = labelledReferrers.find(({ label }) => label === selectedItem);
      if (isEmpty(match)) {
        return;
      }

      const selectedReferrer = match.referrer;
      const id = selectedReferrer.id;
      // Re-picking the referrer already on this record must not swap its provider
      // number for whatever the directory holds now. The record carries the number
      // the referral was actually made under; the directory entry may since have
      // been edited, or be one of several same-named rows.
      const isSameReferrer = Boolean(id) && id === referrer.id;
      let providerNumber = isSameReferrer
        ? referrer.providerNumber ?? selectedReferrer?.providerNumber ?? ""
        : selectedReferrer?.providerNumber ?? "";
      if (id !== referrer.id && !isDataFromPMS) {
        try {
          setIsLoadingProviderNumber(true);
          const referrerLocationsRes = await getReferrerLocationsService({ referrerId: id, clinicId });
          const referrerLocation = get(referrerLocationsRes, "data.referrerLocations", []);
          providerNumber = referrerLocation[0]?.providerNumber || "";
        } catch {}
        setIsLoadingProviderNumber(false);
      }
      updateResource({
        id,
        providerNumber,
        firstName: selectedReferrer.firstName,
        lastName: selectedReferrer.lastName,
        title: selectedReferrer.title,
      });
    },
    [clinicId, referrer.id, referrer.providerNumber, labelledReferrers, updateResource, isDataFromPMS]
  );

  useEffect(() => {
    if (!referrerName || referrer.id) {
      return;
    }
    const matches = labelledReferrers.filter(
      ({ referrer: referrerRecord }) => getReferrerName(referrerRecord).toLowerCase() === referrerName.toLowerCase()
    );
    if (matches.length === 1) {
      onReferrerSelected(matches[0].label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrerName]);

  useEffect(() => {
    setReferrerOptions(labelledReferrers.map(({ label }) => label));
  }, [labelledReferrers]);

  const resetReferrer = useCallback(
    () =>
      updateResource({
        id: "",
        providerNumber: "",
        firstName: "",
        lastName: "",
        title: "",
      }),
    [updateResource]
  );

  const handleReferrerValueChange = useCallback(
    (value) => {
      resetReferrer();
      const needle = value.trim().toLowerCase();
      setReferrerOptions(
        labelledReferrers.filter(({ label }) => label.toLowerCase().includes(needle)).map(({ label }) => label)
      );
    },
    [labelledReferrers, resetReferrer]
  );

  return (
    <>
      <p className="menu-label">REFERRER</p>
      <div className="columns">
        <DatalistField
          isLoading={isLoadingProviderNumber}
          disabled={isLoadingProviderNumber}
          id="referrer"
          className="column is-half"
          defaultValue={referrerName}
          options={referrerOptions}
          onSelect={onReferrerSelected}
          onValueChange={handleReferrerValueChange}
        />
        {!isEmpty(referrerEmail) ? (
          <div className="columns">
            <div className="field column is-one-quarter">
              <label className="label is-small">Email</label>
              <p className="is-size-7">{referrerEmail}</p>
            </div>
          </div>
        ) : null}
      </div>
      {isDataFromPMS && !clinicId ? (
        <p className="help">Select a clinic first — referrers are listed by the clinic they work at.</p>
      ) : null}
      {isCurrentReferrerOutsideClinic ? (
        <p className="help is-danger">
          This referrer is linked to a different clinic. Pick a referrer from the filtered list, or save and provide the
          provider number this referrer uses at the selected clinic.
        </p>
      ) : null}
      <div className="columns">
        <FormTextField
          className="column is-one-quarter"
          value={referrer.providerNumber}
          placeholder="Provide Number"
          onValueChange={handleValueChange("providerNumber")}
        />
        <FormTextField
          className="column is-one-quarter"
          value={referrer.firstName}
          placeholder="First Name"
          onValueChange={handleValueChange("firstName")}
        />
        <FormTextField
          className="column is-one-quarter"
          value={referrer.lastName}
          placeholder="Last Name"
          onValueChange={handleValueChange("lastName")}
        />
        <FormSelectField
          options={TITLE_OPTIONS}
          className="column is-one-quarter"
          value={referrer.title}
          onValueChange={handleValueChange("title")}
        />
      </div>
    </>
  );
};

export default ReferrerForPatientForm;
